import { useCallback, useMemo } from 'react';
import { useToggle } from 'react-use';

import { Pesel } from '@dreryk/personal-external-identifier-common-api';

import { Autosuggest, FormController, TextInput } from '@dreryk/react-components';

import { guardiansTranslationKeys, patientTranslationKeys } from '../../../translations';
import {
   GuardianDetailsFormBean,
   GuardianDetailsModal,
   OnGuardianDetailsFormSubmit,
} from '../GuardianDetailsModal/GuardianDetailsModal';
import { GuardianDetailsModalOpenButton } from '../GuardianDetailsModal/GuardianDetailsModalOpenButton';
import { GuardianModalFormController } from '../GuardianModal.types';

import { patientSearchCreator } from './PatientAutosuggest.hooks';
import { PatientAutosuggestItem } from './PatientAutosuggestItem';
import { StyledPatientAutosuggest } from './StyledPatientAutosuggest';
import { StyledPatientAutosuggestReadOnlyInput } from './StyledPatientAutosuggestReadOnlyInput';

type PatientAutosuggestProps = Pick<GuardianModalFormController, 'watch' | 'register' | 'setValue'> & {
   readonly onGuardianDetailsModalVisibilityToggle: VoidFunction;
   readonly statutoryRepresentative: boolean;
   readonly editMode: boolean;
   readonly patientId?: number;
   readonly patientPesel: Pesel | null | undefined;
   readonly identifyingWithGuardian: boolean | undefined;
};

const patientItemRenderer = (patient: GuardianDetailsFormBean): JSX.Element => (
   <PatientAutosuggestItem patient={patient} />
);

const patientLabelExtractor = (patient: GuardianDetailsFormBean): string => {
   const name = `${patient.lastName ?? ''} ${patient.firstName ?? ''}`.trim();
   if (name !== '') {
      return name;
   }
   if (patient.guardianExternalIdentifierValue) {
      return patient.guardianExternalIdentifierValue;
   }
   if (patient.phoneNumber) {
      return patient.phoneNumber.asString();
   }
   return '';
};

// eslint-disable-next-line max-lines-per-function
export const PatientAutosuggest = (props: PatientAutosuggestProps): JSX.Element => {
   const [guardianDetailsModalVisible, toggleGuardianDetailsModalVisibility] = useToggle(false);
   const { setValue } = props;
   const handleGuardianDetailsSubmit: OnGuardianDetailsFormSubmit = useCallback(
      (guardian): void => {
         setValue('fullName', `${guardian.firstName} ${guardian.lastName}`, {
            shouldDirty: true,
            shouldValidate: true,
         });
         setValue('guardian', guardian, {
            shouldDirty: true,
            shouldValidate: true,
         });
      },
      [setValue]
   );
   const { onGuardianDetailsModalVisibilityToggle } = props;
   const handleGuardianDetailsModalVisibilityToggle = useCallback(() => {
      toggleGuardianDetailsModalVisibility();
      onGuardianDetailsModalVisibilityToggle();
   }, [onGuardianDetailsModalVisibilityToggle, toggleGuardianDetailsModalVisibility]);

   const guardianFieldValidationRules = useMemo(
      () => ({
         required: true,
         validate: (value: unknown) => {
            const guardian = value as GuardianDetailsFormBean;

            if (props.identifyingWithGuardian && guardian.guardianExternalIdentifierType !== 'Pesel') {
               return patientTranslationKeys.guardians.guardianNotUsingPesel;
            }

            if (props.patientPesel && guardian.guardianExternalIdentifierValue === props.patientPesel.asString()) {
               return patientTranslationKeys.guardians.guardianSameAsPatient;
            }

            return undefined;
         },
      }),
      [props.patientPesel, props.identifyingWithGuardian]
   );
   return (
      <>
         <StyledPatientAutosuggest>
            {props.editMode ? (
               <StyledPatientAutosuggestReadOnlyInput>
                  <TextInput
                     disabled
                     label={guardiansTranslationKeys.chosenGuardian}
                     controller={props.register('fullName')}
                     testId={'fullName'}
                  />
                  <FormController controller={props.register('guardian', { required: true })} />
               </StyledPatientAutosuggestReadOnlyInput>
            ) : (
               <Autosuggest<GuardianDetailsFormBean>
                  searchCreator={patientSearchCreator(props.patientId, Boolean(props.identifyingWithGuardian))}
                  noResultsMessage={guardiansTranslationKeys.patientNotFound}
                  label={guardiansTranslationKeys.selectGuardianFromPatientsList}
                  keyProperty={'guardianExternalIdentifierValue'}
                  itemRenderer={patientItemRenderer}
                  labelExtractor={patientLabelExtractor}
                  controller={props.register('guardian', guardianFieldValidationRules)}
                  placeholder={guardiansTranslationKeys.fillPatientDataForSearch}
                  minChars={3}
                  testId={'patientSearch'}
               />
            )}
            <GuardianDetailsModalOpenButton
               label={guardiansTranslationKeys[props.editMode ? 'editGuardianData' : 'addNewGuardian']}
               onClick={handleGuardianDetailsModalVisibilityToggle}
            />
         </StyledPatientAutosuggest>
         {guardianDetailsModalVisible ? (
            <GuardianDetailsModal
               onToggleVisibility={handleGuardianDetailsModalVisibilityToggle}
               statutoryRepresentative={props.statutoryRepresentative}
               onSubmit={handleGuardianDetailsSubmit}
               initialValues={props.watch('guardian')!}
               headerText={guardiansTranslationKeys[props.editMode ? 'editGuardianData' : 'addNewGuardian']}
            />
         ) : null}
      </>
   );
};
